import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Pixel size + stroke — must match root `size-*` classes (`size-8` / `size-12` / `size-16`).
 * Dimensions per EDUN-11528: sm 32, md 48, lg 64.
 */
export const circularProgressDimensions = {
  sm: { px: 32, stroke: 3 },
  md: { px: 48, stroke: 4 },
  lg: { px: 64, stroke: 5 },
} as const;

/**
 * Center label: digits vs `%` use different `Text` variants; scale with ring (32 / 48 / 64).
 * sm/md use the smallest body/label tokens plus a slight UI scale on the wrapper so labels
 * stay inside the stroke (including “72%” / “100%”).
 */
export const circularProgressCenterLabelNumberVariant = {
  sm: 'l2',
  md: 'l1',
  lg: 'b3',
} as const;

export const circularProgressCenterLabelPercentVariant = {
  sm: 'l2',
  md: 'l2',
  lg: 'l1',
} as const;

export const circularProgressRootVariants = cva(
  'inline-grid grid-cols-1 shrink-0 place-items-center',
  {
    variants: {
      size: {
        sm: 'size-8',
        md: 'size-12',
        lg: 'size-16',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

/** Background ring — light tint per variant (`*-200` for brand pairs; semantic variants use `*-100`). */
export const circularProgressTrackVariants = cva('fill-none', {
  variants: {
    variant: {
      primary: 'stroke-primary-200',
      secondary: 'stroke-secondary-200',
      success: 'stroke-success-100',
      warning: 'stroke-warning-100',
      error: 'stroke-error-100',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});

export const circularProgressIndicatorVariants = cva('fill-none', {
  variants: {
    variant: {
      primary: 'stroke-primary-700',
      secondary: 'stroke-secondary-700',
      success: 'stroke-success-500',
      warning: 'stroke-warning-900',
      error: 'stroke-error-500',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
});

/** Center label wrapper — stacks in the root grid cell with the SVG; vertically centers `%` with the numerals. */
export const circularProgressCenterLabelVariants = cva(
  'pointer-events-none col-start-1 row-start-1 flex origin-center items-center justify-center gap-0 whitespace-nowrap tabular-nums tracking-tighter',
  {
    variants: {
      size: {
        sm: 'scale-circular-progress-label-sm',
        md: 'scale-circular-progress-label-md',
        lg: 'scale-100',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export type CircularProgressCenterLabelVariantProps = VariantProps<
  typeof circularProgressCenterLabelVariants
>;

export type CircularProgressRootVariantProps = VariantProps<
  typeof circularProgressRootVariants
>;
export type CircularProgressIndicatorVariantProps = VariantProps<
  typeof circularProgressIndicatorVariants
>;
